import React, { useRef, useEffect, useCallback } from 'react'

/* ── Tune the auto-scroll here ──────────────────────────────
   SCROLL_SPEED is pixels per millisecond. LOWER = slower.
   0.05 ≈ 50px/sec, a calm reading pace.
   0.03 is a slow crawl; 0.08 is a brisk skim.                */
const SCROLL_SPEED = 0.05
const MIN_DURATION = 1500      // ms floor for very short pages
const MAX_DURATION = 600000    // ms ceiling (10 min) — high so reading pace is never clamped
const AUTOSTART_DELAY = 600    // ms grace after the button is in view, before it begins
const VISIBLE_RATIO = 0.8      // how much of the scroll button must be in view to trigger (0–1)
/* ──────────────────────────────────────────────────────────── */

const ScrollDown = () => {
  const rafRef = useRef(null)
  const cleanupRef = useRef(() => {})
  const startedRef = useRef(false)
  const desktopRef = useRef(null)
  const mobileRef = useRef(null)

  const stopAutoScroll = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    cleanupRef.current()
    cleanupRef.current = () => {}
  }, [])

  const runAutoScroll = useCallback(() => {
    stopAutoScroll() // restart cleanly if already running

    const footer = document.querySelector('.footer')
    const maxScroll =
      document.documentElement.scrollHeight - window.innerHeight
    const target = footer
      ? Math.min(footer.getBoundingClientRect().top + window.scrollY, maxScroll)
      : maxScroll

    const start = window.scrollY
    const distance = target - start
    if (distance <= 0) return

    const duration = Math.min(
      MAX_DURATION,
      Math.max(MIN_DURATION, distance / SCROLL_SPEED)
    )

    // Force instant per-frame scrolling so a global `scroll-behavior: smooth`
    // can't fight the loop.
    const html = document.documentElement
    const prevBehavior = html.style.scrollBehavior
    html.style.scrollBehavior = 'auto'

    // Any genuine user input cancels the auto-scroll. (Not the `scroll` event —
    // our own scrolling fires that.)
    const interrupt = () => stopAutoScroll()
    const opts = { passive: true }
    const arm = () => {
      window.addEventListener('wheel', interrupt, opts)
      window.addEventListener('touchstart', interrupt, opts)
      window.addEventListener('touchmove', interrupt, opts)
      window.addEventListener('mousedown', interrupt, opts)
      window.addEventListener('keydown', interrupt)
    }
    // Arm a tick late so the click/keypress that started it doesn't cancel it.
    const armTimer = setTimeout(arm, 0)

    cleanupRef.current = () => {
      clearTimeout(armTimer)
      window.removeEventListener('wheel', interrupt, opts)
      window.removeEventListener('touchstart', interrupt, opts)
      window.removeEventListener('touchmove', interrupt, opts)
      window.removeEventListener('mousedown', interrupt, opts)
      window.removeEventListener('keydown', interrupt)
      html.style.scrollBehavior = prevBehavior
    }

    let startTime = null
    const step = (now) => {
      if (startTime === null) startTime = now
      const t = Math.min((now - startTime) / duration, 1)
      // Linear: one steady pace the whole way, so reading speed never changes.
      window.scrollTo(0, start + distance * t)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step)
      } else {
        stopAutoScroll()
      }
    }
    rafRef.current = requestAnimationFrame(step)
  }, [stopAutoScroll])

  const handleScrollToFooter = useCallback(
    (e) => {
      if (e) e.preventDefault()
      runAutoScroll()
    },
    [runAutoScroll]
  )

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleScrollToFooter(e)
  }

  // Auto-start when the scroll button is close — i.e. mostly in view. Fires once
  // per load. Respects the reduced-motion accessibility setting.
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (prefersReduced) return

    let timer = null
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= VISIBLE_RATIO &&
            !startedRef.current
          ) {
            startedRef.current = true
            observer.disconnect()
            timer = setTimeout(runAutoScroll, AUTOSTART_DELAY)
            break
          }
        }
      },
      { threshold: [VISIBLE_RATIO] }
    )

    // Observe whichever indicator is rendered for this breakpoint.
    if (desktopRef.current) observer.observe(desktopRef.current)
    if (mobileRef.current) observer.observe(mobileRef.current)

    return () => {
      observer.disconnect()
      if (timer) clearTimeout(timer)
      stopAutoScroll()
    }
  }, [runAutoScroll, stopAutoScroll])

  return (
    <>
      {/* Desktop scroll indicator */}
      <div className="home__scroll" ref={desktopRef}>
        <a
          role="button"
          tabIndex={0}
          aria-label="Scroll to footer"
          onClick={handleScrollToFooter}
          onKeyDown={handleKeyDown}
          className="home__scroll-button button--flex"
          style={{ cursor: 'pointer' }}
        >
          <svg
            width="32px"
            height="32px"
            className="home__scroll-mouse"
            viewBox="0 0 247 390"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{
              fillRule: "evenodd",
              clipRule: "evenodd",
              strokeLinecap: "round",
              strokeLinejoin: "round",
              strokeMiterlimit: "1.5",
            }}
          >
            <path
              className="wheel"
              d="M123.359,79.775l0,72.843"
              style={{
                fill: "none",
                stroke: "var(--title-color)",
                strokeWidth: "20px",
              }}
            ></path>
            <path
              id="mouse"
              d="M236.717,123.359c0,-62.565 -50.794,-113.359 -113.358,-113.359c-62.565,0 -113.359,50.794 -113.359,113.359l0,143.237c0,62.565 50.794,113.359 113.359,113.359c62.564,0 113.358,-50.794 113.358,-113.359l0,-143.237Z"
              style={{
                fill: "none",
                stroke: "var(--title-color)",
                strokeWidth: "20px",
              }}
            ></path>
          </svg>
          <span className="home__scroll-name">Scroll Down</span>
          <div className="home__scroll-arrows">
            <i className="uil uil-arrow-down home__scroll-arrow"></i>
          </div>
        </a>
      </div>

      {/* Mobile scroll indicator */}
      <div className="home__scroll-indicator" ref={mobileRef}>
        <a
          role="button"
          tabIndex={0}
          aria-label="Scroll to footer"
          onClick={handleScrollToFooter}
          onKeyDown={handleKeyDown}
          style={{ cursor: 'pointer' }}
        >
          <div className="scroll-icon">
            <div className="scroll-wheel"></div>
          </div>
          <span className="home__scroll-text">Scroll Down</span>
          <div className="home__scroll-arrows">
            <i className="uil uil-arrow-down home__scroll-arrow"></i>
          </div>
        </a>
      </div>
    </>
  )
}

export default ScrollDown